import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Stethoscope, HeartPulse, Syringe, Users, Brain, Baby, GraduationCap, Briefcase,
  FlaskConical, ShieldCheck, Home as HomeIcon, Award, Globe2, Landmark, ArrowUpRight, Sparkles, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings, assetUrl } from "@/context/SettingsContext";

const iconMap = {
  Stethoscope, HeartPulse, Syringe, Users, Brain, Baby, GraduationCap, Briefcase,
  FlaskConical, ShieldCheck, Home: HomeIcon, Award,
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Careers() {
  const { settings } = useSettings();
  const c = settings?.careers || {};
  const roles = c.roles || [];
  const gov = c.governmentTags || [];
  const intl = c.internationalCountries || [];

  return (
    <div data-testid="careers-page">
      {/* HERO */}
      <section className="relative overflow-hidden py-20 lg:py-28 bg-white border-b border-slate-100">
        <div aria-hidden className="absolute -top-40 -right-32 w-[520px] h-[520px] rounded-full opacity-15 blur-3xl animate-blob"
             style={{ background: "radial-gradient(circle, var(--brand-primary), transparent 60%)" }} />
        <div aria-hidden className="absolute inset-0 bg-blueprint opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 shadow-sm pl-1 pr-4 py-1 mb-6">
              <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-white" style={{ backgroundColor: "var(--brand-secondary)" }}>Careers</span>
              <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 brand-accent-text" /> {c.eyebrow}
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight">
              Your Gateway to a <span className="text-gradient-brand italic">Rewarding</span> Nursing Career
            </h1>
            <p className="text-slate-600 text-lg mt-6 leading-relaxed max-w-2xl">{c.intro}</p>
          </motion.div>
          {c.image && (
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)]">
                <img src={assetUrl(c.image)} alt="Nursing career opportunities" className="w-full h-[380px] object-cover" />
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Career Paths grid */}
      {roles.length > 0 && (
        <section className="py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">Where Our Graduates Work</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">{c.rolesHeading || "Career Paths"}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {roles.map((r, i) => {
                const Icon = iconMap[r.icon] || Stethoscope;
                return (
                  <motion.div
                    key={r.title}
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: (i % 6) * 0.05 }}
                    data-testid={`role-card-${i}`}
                    className="group relative bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all overflow-hidden"
                  >
                    <div aria-hidden className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity blur-2xl"
                         style={{ background: "radial-gradient(circle, var(--brand-secondary), transparent 60%)" }} />
                    <div className="relative flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0 transition-transform group-hover:scale-110"
                           style={{ backgroundColor: "rgba(30,58,138,0.08)", color: "var(--brand-primary)" }}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg text-slate-900 leading-snug">{r.title}</h3>
                        <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Actively hiring nationwide
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Government + International */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Government */}
          {gov.length > 0 && (
            <motion.div {...fadeUp} data-testid="government-card"
                        style={{ backgroundColor: "var(--brand-primary)" }}
                        className="text-white rounded-3xl p-10 shadow-[0_30px_60px_-20px_rgba(30,58,138,0.4)] relative overflow-hidden">
              <div aria-hidden className="absolute inset-0 bg-grain-dark opacity-30" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-11 h-11 rounded-xl bg-white/10 grid place-items-center">
                    <Landmark className="w-5 h-5" />
                  </span>
                  <p className="text-xs font-bold uppercase tracking-[0.24em]" style={{ color: "var(--brand-secondary)" }}>Government Sector</p>
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl leading-snug">{c.governmentHeading}</h2>
                <p className="text-white/80 mt-4 leading-relaxed">{c.governmentIntro}</p>
                <ul className="mt-6 flex flex-wrap gap-2.5">
                  {gov.map((g) => (
                    <li key={g} className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-4 py-2 text-sm font-medium">
                      <Check className="w-3.5 h-3.5 text-emerald-400" /> {g}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}

          {/* International */}
          {intl.length > 0 && (
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} data-testid="international-card"
                        className="bg-white rounded-3xl p-10 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] relative overflow-hidden">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: "rgba(244,63,94,0.08)", color: "var(--brand-secondary)" }}>
                  <Globe2 className="w-5 h-5" />
                </span>
                <p className="text-xs font-bold uppercase tracking-[0.24em] brand-secondary-text">International</p>
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl leading-snug text-slate-900">{c.internationalHeading}</h2>
              <p className="text-slate-600 mt-4 leading-relaxed">{c.internationalIntro}</p>
              <ul className="mt-6 grid grid-cols-2 gap-3">
                {intl.map((country) => (
                  <li key={country.name} className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                    <span className="text-2xl leading-none">{country.flag}</span>
                    <span className="text-sm font-medium text-slate-800">{country.name}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-400 mt-4">* Subject to licensing requirements in destination country</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Closing CTA (info-only, no apply form) */}
      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 {...fadeUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
            {c.closingHeading}
          </motion.h2>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="text-slate-600 text-lg mt-4">
            {c.closingText}
          </motion.p>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.16 }} className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/courses" data-testid="careers-explore-courses">
              <Button style={{ backgroundColor: "var(--brand-primary)" }} className="rounded-full h-12 px-7 text-white hover:brightness-110 hover:-translate-y-0.5 transition-all">
                Explore Programmes <ArrowUpRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/placements" data-testid="careers-view-placements">
              <Button variant="outline" className="rounded-full h-12 px-7 border-2 bg-white/60 backdrop-blur"
                      style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}>
                View Placement Details
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
